import {useQuery} from "@tanstack/react-query";

import {useGetHasPermissions} from "@/src/modules/core/store/me";

import {
  VEHICLE_CATALOG_SEARCH_MIN_CHARS,
  vehicleCatalogModelItems,
} from "../lib/vehicle-catalog-search";
import {vehiclesService} from "../services";

export {VEHICLE_CATALOG_SEARCH_MIN_CHARS as VEHICLE_MODEL_SEARCH_MIN_CHARS};

/**
 * Server-backed model search (`GET vehiclemodel`).
 * With make: first page loads on make alone; optional `filter[name]` when search ≥2 chars.
 * Without make: name-only search when search ≥2 chars.
 */
export function useVehicleModelsSearch(
  vehicleMakeId: number | null | undefined,
  searchText: string,
) {
  const getHasPermission = useGetHasPermissions();
  const catalogEnabled =
    getHasPermission("vehicle store") ||
    getHasPermission("vehicle update") ||
    getHasPermission("companyvehicle store") ||
    getHasPermission("companyvehicle update");

  const id =
    vehicleMakeId != null && Number.isFinite(vehicleMakeId) && vehicleMakeId > 0
      ? vehicleMakeId
      : null;

  const q = searchText.trim().slice(0, 150);
  const hasMake = id != null;
  const hasNameQuery = q.length >= VEHICLE_CATALOG_SEARCH_MIN_CHARS;
  const enabled = catalogEnabled && (hasMake || hasNameQuery);

  const apiName = hasNameQuery ? q : undefined;

  const query = useQuery({
    queryKey: ["vehicle-models", "search", id, apiName ?? ""],
    queryFn: () => vehiclesService.searchVehicleModels(id, apiName),
    enabled,
    retry: false,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    items: vehicleCatalogModelItems(id, searchText, query.data),
  };
}

/** @deprecated Use `useVehicleModelsSearch` — name-only path is the same hook with no make id. */
export function useVehicleModelsNameOnlySearch(
  vehicleMakeId: number | null | undefined,
  searchText: string,
) {
  return useVehicleModelsSearch(vehicleMakeId, searchText);
}
